import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ScrollText, Loader2, CheckCircle2, AlertTriangle, MinusCircle, FilePlus2, Truck, XCircle, Send,
} from "lucide-react";

const CUR_SYMBOL = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };
const CURRENCIES = ["TRY", "USD", "EUR", "GBP"];
const TAX_RATES = [0, 1, 8, 10, 18, 20];
const PO_STATUS_LABEL = { draft: "Taslak", ordered: "Sipariş Verildi", partial_received: "Kısmi Geldi", received: "Tamamlandı", cancelled: "İptal" };
const PO_STATUS_TONE = {
  ordered: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  partial_received: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  received: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
};
const RESULT_META = {
  uyumlu: { label: "UYUMLU", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40", Icon: CheckCircle2 },
  fark_var: { label: "FARK VAR", cls: "bg-rose-500/15 text-rose-300 border-rose-500/40", Icon: AlertTriangle },
  fatura_yok: { label: "FATURA YOK", cls: "bg-slate-500/15 text-slate-300 border-slate-500/40", Icon: MinusCircle },
};
const LINE_STATUS_META = {
  ok: { label: "Uyumlu", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  diff: { label: "Fark Var", cls: "bg-rose-500/15 text-rose-300 border-rose-500/40" },
  pending: { label: "Fatura Yok", cls: "bg-slate-500/15 text-slate-400 border-slate-600/40" },
};

const APPR_META = {
  bekliyor: { label: "Bekliyor", cls: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  onaylandi: { label: "Onaylandı", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  reddedildi: { label: "Reddedildi", cls: "bg-rose-500/15 text-rose-300 border-rose-500/40" },
};
const BH_META = {
  not_sent: { label: "BH: Gönderilmedi", cls: "bg-slate-500/15 text-slate-400 border-slate-600/40" },
  sending: { label: "BH: Gönderiliyor", cls: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
  sent: { label: "BH: Gönderildi", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" },
  failed: { label: "BH: Hata", cls: "bg-rose-500/15 text-rose-300 border-rose-500/40" },
  unverified: { label: "BH: Doğrulanamadı", cls: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
};

const fmt = (n) => Number(n || 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQty = (n) => Number(n || 0).toLocaleString("tr-TR", { maximumFractionDigits: 3 });
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("tr-TR") : "—");
const parseTr = (v) => {
  if (v == null) return NaN;
  let s = String(v).trim();
  if (!s) return NaN;
  if (s.includes(",")) s = s.replace(/\./g, "").replace(",", ".");
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

export default function InvoiceCheck() {
  const [pos, setPos] = useState([]);
  const [loadingPos, setLoadingPos] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [check, setCheck] = useState(null);
  const [loadingCheck, setLoadingCheck] = useState(false);
  const [invOpen, setInvOpen] = useState(false);
  const [invForm, setInvForm] = useState({ invoice_no: "", invoice_date: new Date().toISOString().slice(0, 10), note: "" });
  const [invRows, setInvRows] = useState({});
  const [saving, setSaving] = useState(false);
  const { can } = useAuth();
  const canApprove = can("finance", "edit");
  const [actDlg, setActDlg] = useState({ open: false, mode: "", invoice: null });
  const [actNote, setActNote] = useState("");
  const [actSaving, setActSaving] = useState(false);
  const [sendingId, setSendingId] = useState(null);

  const loadPos = useCallback(async () => {
    setLoadingPos(true);
    try {
      const { data } = await api.get("/purchase-orders");
      setPos((data || []).filter((p) => ["ordered", "partial_received", "received"].includes(p.status || "")));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoadingPos(false); }
  }, []);

  const loadCheck = useCallback(async (poId) => {
    setLoadingCheck(true);
    try {
      const { data } = await api.get(`/purchase-orders/${poId}/invoice-check`);
      setCheck(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoadingCheck(false); }
  }, []);

  useEffect(() => { loadPos(); }, [loadPos]);

  const openPo = (id) => { setSelectedId(id); loadCheck(id); };

  const openInvoiceDialog = () => {
    if (!check) return;
    const seed = {};
    (check.lines || []).forEach((l) => {
      seed[l.po_item_id] = {
        qty: l.unbilled_received > 0 ? String(l.unbilled_received).replace(".", ",") : "",
        unit_price: String(l.po_unit_price ?? 0).replace(".", ","),
        currency: l.po_currency || "TRY",
        tax_rate: String(l.po_tax_rate ?? 0),
      };
    });
    setInvRows(seed);
    setInvForm({ invoice_no: "", invoice_date: new Date().toISOString().slice(0, 10), note: "" });
    setInvOpen(true);
  };

  const setRow = (id, patch) => setInvRows((r) => ({ ...r, [id]: { ...r[id], ...patch } }));

  const invTotals = useMemo(() => {
    let sub = 0, tax = 0;
    for (const l of (check?.lines || [])) {
      const r = invRows[l.po_item_id];
      if (!r) continue;
      const q = parseTr(r.qty);
      const up = parseTr(r.unit_price);
      if (!Number.isFinite(q) || q <= 0 || !Number.isFinite(up)) continue;
      const net = q * up;
      sub += net;
      tax += net * (Number(r.tax_rate || 0) / 100);
    }
    return { sub, tax, total: sub + tax };
  }, [invRows, check]);

  const submitInvoice = async () => {
    if (!check) return;
    const lines = [];
    for (const l of check.lines) {
      const r = invRows[l.po_item_id] || {};
      const q = parseTr(r.qty);
      if (!Number.isFinite(q) || q <= 0) continue;
      const up = parseTr(r.unit_price);
      if (!Number.isFinite(up) || up < 0) { toast.error(`Geçersiz birim fiyat: ${l.product_name || l.material_name}`); return; }
      lines.push({ po_item_id: l.po_item_id, quantity: q, unit_price: up, currency: r.currency || "TRY", tax_rate: Number(r.tax_rate || 0) });
    }
    if (!invForm.invoice_no.trim()) { toast.error("Fatura no zorunlu"); return; }
    if (!lines.length) { toast.error("En az bir satırda miktar girin"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/purchase-invoices", {
        purchase_order_id: check.po_id,
        invoice_no: invForm.invoice_no.trim(),
        invoice_date: invForm.invoice_date ? new Date(invForm.invoice_date).toISOString() : null,
        note: invForm.note,
        lines,
      });
      const res = data.check?.result;
      if (res === "uyumlu") toast.success(`Fatura ${data.invoice.invoice_no} kaydedildi — UYUMLU`);
      else if (res === "fark_var") toast.warning(`Fatura ${data.invoice.invoice_no} kaydedildi — FARK VAR`);
      else toast.success(`Fatura ${data.invoice.invoice_no} kaydedildi`);
      setInvOpen(false);
      loadCheck(check.po_id);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const openAction = (mode, invoice) => { setActDlg({ open: true, mode, invoice }); setActNote(""); };

  const submitAction = async () => {
    const inv = actDlg.invoice;
    if (!inv || !check) return;
    if (actDlg.mode === "reject" && !actNote.trim()) { toast.error("Ret sebebi zorunlu"); return; }
    setActSaving(true);
    try {
      const { data } = await api.post(`/purchase-invoices/${inv.id}/${actDlg.mode}`, { note: actNote.trim() });
      if (actDlg.mode === "approve") {
        if (data.check_result === "fark_var") toast.warning(`Fatura ${inv.invoice_no} fark notu ile manuel onaylandı`);
        else toast.success(`Fatura ${inv.invoice_no} onaylandı`);
      } else {
        toast.success(`Fatura ${inv.invoice_no} reddedildi`);
      }
      setActDlg({ open: false, mode: "", invoice: null });
      setActNote("");
      loadCheck(check.po_id);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setActSaving(false); }
  };

  const sendBH = async (inv) => {
    if (!check) return;
    const unv = inv.bh_send_status === "unverified";
    if (!window.confirm(unv ? `${inv.invoice_no}: önceki gönderimin sonucu doğrulanamadı. Bizim Hesap'ta bu faturanın OLMADIĞINI kontrol ettiyseniz tekrar gönderin. Devam?` : `${inv.invoice_no} Bizim Hesap'a alış faturası olarak gönderilecek. Devam?`)) return;
    setSendingId(inv.id);
    try {
      const { data } = await api.post(`/purchase-invoices/${inv.id}/send-bizim-hesap${unv ? "?force=true" : ""}`);
      toast.success(`Bizim Hesap'a gönderildi (ref: ${data.bh_ref || "-"})`);
      loadCheck(check.po_id);
    } catch (e) { toast.error(formatApiError(e)); loadCheck(check.po_id); }
    finally { setSendingId(null); }
  };

  const meta = check ? RESULT_META[check.result] : null;

  return (
    <div data-testid="invoice-check-page">
      <PageHeader
        title="Fatura Kontrol"
        subtitle="Teklif → Sipariş → Mal Kabul → Gelen Fatura karşılaştırması"
        breadcrumbs={["Modüller", "Satın Alma", "Fatura Kontrol"]}
      />

      <div className="p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4">
        {/* PO listesi */}
        <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden h-fit" data-testid="ic-po-list">
          <div className="px-4 py-2.5 border-b border-slate-800 text-[11px] font-mono text-slate-500 uppercase tracking-widest">
            Satın Alma Siparişleri
          </div>
          {loadingPos ? (
            <div className="flex justify-center py-16"><Loader2 className="w-5 h-5 text-blue-500 animate-spin" /></div>
          ) : pos.length === 0 ? (
            <div className="text-center py-12 text-slate-500 text-sm">Açık sipariş yok.</div>
          ) : (
            <div className="max-h-[70vh] overflow-auto divide-y divide-slate-800/60">
              {pos.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`ic-po-row-${p.po_no}`}
                  onClick={() => openPo(p.id)}
                  className={`w-full text-left px-4 py-3 hover:bg-slate-800/50 transition-colors ${selectedId === p.id ? "bg-slate-800/70 border-l-2 border-blue-500" : ""}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-sm text-blue-400 font-bold">{p.po_no}</span>
                    <Badge className={`text-[10px] border ${PO_STATUS_TONE[p.status] || "bg-slate-500/15 text-slate-300 border-slate-500/40"}`}>
                      {PO_STATUS_LABEL[p.status] || p.status}
                    </Badge>
                  </div>
                  <div className="text-xs text-slate-300 truncate mt-0.5 flex items-center gap-1.5">
                    <Truck className="w-3 h-3 text-slate-500" />{p.supplier_name || "—"}
                  </div>
                  <div className="text-[10px] font-mono text-slate-500 mt-0.5">{fmtDate(p.created_at)} · ₺{fmt(p.total_price)}</div>
                </button>
              ))}
            </div>
          )}
        </Card>

        {/* Karşılaştırma paneli */}
        <div className="space-y-4 min-w-0">
          {!selectedId ? (
            <Card className="bg-slate-900 border-slate-800 rounded-sm p-16 text-center text-slate-500 text-sm" data-testid="ic-empty">
              <ScrollText className="w-10 h-10 mx-auto text-slate-700 mb-3" />
              Karşılaştırmak için soldan bir satın alma siparişi seçin.
            </Card>
          ) : loadingCheck ? (
            <Card className="bg-slate-900 border-slate-800 rounded-sm p-16 flex justify-center"><Loader2 className="w-6 h-6 text-blue-500 animate-spin" /></Card>
          ) : check && meta && (
            <>
              <Card className="bg-slate-900 border-slate-800 rounded-sm p-4 flex flex-wrap items-center gap-3" data-testid="ic-summary">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-lg text-blue-400 font-bold">{check.po_no}</span>
                    <Badge className={`text-xs border ${meta.cls}`} data-testid="ic-result-badge">
                      <meta.Icon className="w-3.5 h-3.5 mr-1" />{meta.label}
                    </Badge>
                  </div>
                  <div className="text-xs text-slate-400 mt-1">{check.supplier_name} · Termin: {fmtDate(check.expected_delivery_date)}</div>
                </div>
                <div className="flex items-center gap-5 font-mono text-xs text-slate-500">
                  <span>Sipariş <span className="text-slate-200 tabular-nums">{fmtQty(check.totals.ordered)}</span></span>
                  <span>Mal Kabul <span className="text-slate-200 tabular-nums">{fmtQty(check.totals.received)}</span></span>
                  <span>Faturalanan <span className="text-slate-200 tabular-nums">{fmtQty(check.totals.invoiced)}</span></span>
                </div>
                <Button onClick={openInvoiceDialog} className="bg-blue-600 hover:bg-blue-500 rounded-sm uppercase tracking-wider text-xs font-semibold" data-testid="ic-add-invoice-btn">
                  <FilePlus2 className="w-4 h-4 mr-2" /> Fatura Gir
                </Button>
              </Card>

              {/* Karşılaştırma tablosu */}
              <Card className="bg-slate-900 border-slate-800 rounded-sm overflow-hidden" data-testid="ic-lines">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent border-slate-800">
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Kalem</TableHead>
                      <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Satın Alma Siparişi (Miktar · Fiyat · KDV)</TableHead>
                      <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Mal Kabul</TableHead>
                      <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Faturalanan</TableHead>
                      <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Durum</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {check.lines.map((l) => {
                      const sm = LINE_STATUS_META[l.status];
                      return (
                        <TableRow key={l.po_item_id} className="border-slate-800" data-testid={`ic-line-${l.po_item_id}`}>
                          <TableCell>
                            <div className="text-slate-100 text-sm font-medium">{l.product_name || l.material_name || "—"}</div>
                            <div className="text-[10px] font-mono text-slate-500 mt-0.5">{l.variant_sku || l.kind}</div>
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs tabular-nums">
                            <div className="text-slate-100">{fmtQty(l.ordered_qty)} {l.unit}</div>
                            <div className="text-slate-400">{CUR_SYMBOL[l.po_currency] || ""}{fmt(l.po_unit_price)} {l.po_currency} · %{l.po_tax_rate}</div>
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs tabular-nums text-slate-100">
                            {fmtQty(l.received_qty_total)} {l.unit}
                            {l.unbilled_received > 0 && <div className="text-[10px] text-amber-400">faturalanmamış: {fmtQty(l.unbilled_received)}</div>}
                          </TableCell>
                          <TableCell className="text-right font-mono text-xs tabular-nums">
                            <div className="text-slate-100">{fmtQty(l.invoiced_qty)} {l.unit}</div>
                            {(l.invoice_lines || []).map((il) => (
                              <div key={il.id} className="text-[10px] text-slate-500">
                                {il.invoice_no}: {fmtQty(il.quantity)} × {CUR_SYMBOL[il.currency] || ""}{fmt(il.unit_price)} {il.currency} · %{il.tax_rate}
                              </div>
                            ))}
                          </TableCell>
                          <TableCell>
                            <Badge className={`text-[10px] border ${sm.cls}`} data-testid={`ic-line-status-${l.po_item_id}`}>{sm.label}</Badge>
                            {l.diffs.map((d, i) => (
                              <div key={i} className="text-[10px] text-rose-400 mt-1" data-testid={`ic-diff-${l.po_item_id}-${d.field}`}>
                                {d.label}{d.diff !== "" && d.diff != null ? `: ${d.field === "price" ? (d.diff > 0 ? "+" : "") + fmt(d.diff) : fmtQty(d.diff)}` : `: Satın Alma Siparişi ${d.po} ≠ Fatura ${d.invoice}`}
                              </div>
                            ))}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Card>

              {/* Teklif referansı */}
              {check.quotes.length > 0 && (
                <Card className="bg-slate-900 border-slate-800 rounded-sm p-4" data-testid="ic-quotes-ref">
                  <div className="text-[11px] font-mono text-slate-500 uppercase tracking-widest mb-2">Teklif Referansı (salt-bilgi)</div>
                  <div className="flex flex-wrap gap-2">
                    {check.quotes.map((q) => (
                      <div key={q.id} className="rounded-sm border border-slate-800 bg-slate-950/60 px-3 py-2 text-xs">
                        <span className="font-mono text-blue-400">{q.quote_no || q.id.slice(0, 8)}</span>
                        <span className="text-slate-400"> · {q.supplier_name} · {fmtQty(q.quantity)} × {CUR_SYMBOL[q.currency] || ""}{fmt(q.unit_price)} {q.currency} · %{q.tax_rate}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Gelen faturalar */}
              {check.invoices.length > 0 && (
                <Card className="bg-slate-900 border-slate-800 rounded-sm p-4" data-testid="ic-invoices">
                  <div className="text-[11px] font-mono text-slate-500 uppercase tracking-widest mb-2">Gelen Faturalar · Muhasebe Onayı</div>
                  <div className="space-y-2">
                    {check.invoices.map((inv) => {
                      const appr = APPR_META[inv.approval_status || "bekliyor"];
                      const bh = BH_META[inv.bh_send_status || "not_sent"];
                      const bhSent = (inv.bh_send_status || "not_sent") === "sent";
                      return (
                        <div key={inv.id} className="rounded-sm border border-slate-800 bg-slate-950/60 px-3 py-2.5 text-xs" data-testid={`ic-invoice-${inv.id}`}>
                          <div className="flex items-center justify-between gap-2 flex-wrap">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-mono text-slate-200 font-semibold">{inv.invoice_no}</span>
                              <Badge className={`text-[10px] border ${appr.cls}`} data-testid={`ic-appr-${inv.id}`}>{appr.label}</Badge>
                              <Badge className={`text-[10px] border ${bh.cls}`} data-testid={`ic-bh-${inv.id}`}>{bh.label}</Badge>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-slate-400 font-mono">{fmtDate(inv.invoice_date)} · {(inv.lines || []).length} satır</span>
                              <span className="font-mono text-slate-100 tabular-nums">₺{fmt(inv.total_price)}</span>
                            </div>
                          </div>
                          {(inv.approval_note || inv.rejection_note || inv.bh_ref || inv.bh_error) && (
                            <div className="mt-1.5 text-[10px] text-slate-500 font-mono space-y-0.5">
                              {inv.approval_note && <div data-testid={`ic-appr-note-${inv.id}`}>Onay notu: {inv.approval_note}{inv.check_result_at_approval === "fark_var" ? " (farklı onay)" : ""}</div>}
                              {inv.rejection_note && <div className="text-rose-400" data-testid={`ic-rej-note-${inv.id}`}>Ret sebebi: {inv.rejection_note}</div>}
                              {inv.bh_ref && <div data-testid={`ic-bh-ref-${inv.id}`}>BH Ref: {inv.bh_ref}</div>}
                              {inv.bh_error && <div className="text-rose-400" data-testid={`ic-bh-err-${inv.id}`}>BH Hata: {inv.bh_error}</div>}
                            </div>
                          )}
                          {canApprove && !bhSent && (
                            <div className="mt-2 flex items-center gap-2">
                              {(inv.approval_status || "bekliyor") !== "onaylandi" && (
                                <Button size="sm" variant="outline" onClick={() => openAction("approve", inv)} data-testid={`ic-approve-${inv.id}`}
                                  className="h-7 text-[10px] border-emerald-700 text-emerald-300 hover:bg-emerald-950/40 rounded-sm">
                                  <CheckCircle2 className="w-3 h-3 mr-1" /> Onayla
                                </Button>
                              )}
                              {(inv.approval_status || "bekliyor") !== "reddedildi" && (
                                <Button size="sm" variant="outline" onClick={() => openAction("reject", inv)} data-testid={`ic-reject-${inv.id}`}
                                  className="h-7 text-[10px] border-rose-700 text-rose-300 hover:bg-rose-950/40 rounded-sm">
                                  <XCircle className="w-3 h-3 mr-1" /> Reddet
                                </Button>
                              )}
                              {(inv.approval_status || "bekliyor") === "onaylandi" && (
                                <Button size="sm" onClick={() => sendBH(inv)} disabled={sendingId === inv.id} data-testid={`ic-send-bh-${inv.id}`}
                                  className="h-7 text-[10px] bg-blue-600 hover:bg-blue-500 rounded-sm">
                                  {sendingId === inv.id ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                                  Bizim Hesap'a Gönder
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </Card>
              )}
            </>
          )}
        </div>
      </div>

      {/* Fatura giriş dialogu */}
      <Dialog open={invOpen} onOpenChange={setInvOpen}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-3xl" data-testid="ic-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-100">Gelen Fatura Girişi — {check?.po_no}</DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">
              {check?.supplier_name}. Kısmi fatura girebilirsiniz; miktarlar mal kabul ile karşılaştırılır.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="space-y-1.5 col-span-2">
                <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Fatura No *</Label>
                <Input data-testid="ic-inv-no" value={invForm.invoice_no} onChange={(e) => setInvForm({ ...invForm, invoice_no: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm h-9 font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Fatura Tarihi</Label>
                <Input data-testid="ic-inv-date" type="date" value={invForm.invoice_date} onChange={(e) => setInvForm({ ...invForm, invoice_date: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm h-9 font-mono" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Not</Label>
                <Input data-testid="ic-inv-note" value={invForm.note} onChange={(e) => setInvForm({ ...invForm, note: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm h-9" />
              </div>
            </div>

            <div className="rounded-sm border border-slate-800 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-slate-800">
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">Kalem</TableHead>
                    <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono">Faturalanmamış (Mal Kabul)</TableHead>
                    <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono w-28">Miktar</TableHead>
                    <TableHead className="text-right text-[10px] uppercase tracking-widest text-slate-500 font-mono w-28">Birim Fiyat</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono w-24">PB</TableHead>
                    <TableHead className="text-[10px] uppercase tracking-widest text-slate-500 font-mono w-24">KDV %</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(check?.lines || []).map((l) => {
                    const r = invRows[l.po_item_id] || {};
                    return (
                      <TableRow key={l.po_item_id} className="border-slate-800" data-testid={`ic-inv-row-${l.po_item_id}`}>
                        <TableCell>
                          <div className="text-slate-100 text-sm">{l.product_name || l.material_name || "—"}</div>
                          <div className="text-[10px] font-mono text-slate-500">{l.variant_sku || l.kind}</div>
                        </TableCell>
                        <TableCell className="text-right font-mono text-xs text-slate-300 tabular-nums">{fmtQty(l.unbilled_received)} {l.unit}</TableCell>
                        <TableCell><Input data-testid={`ic-inv-qty-${l.po_item_id}`} type="text" inputMode="decimal" value={r.qty || ""} onChange={(e) => setRow(l.po_item_id, { qty: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm h-8 font-mono text-right" /></TableCell>
                        <TableCell><Input data-testid={`ic-inv-price-${l.po_item_id}`} type="text" inputMode="decimal" value={r.unit_price || ""} onChange={(e) => setRow(l.po_item_id, { unit_price: e.target.value })} className="bg-slate-950 border-slate-700 rounded-sm h-8 font-mono text-right" /></TableCell>
                        <TableCell>
                          <Select value={r.currency || "TRY"} onValueChange={(v) => setRow(l.po_item_id, { currency: v })}>
                            <SelectTrigger data-testid={`ic-inv-cur-${l.po_item_id}`} className="bg-slate-950 border-slate-700 rounded-sm h-8"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                              {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <Select value={String(r.tax_rate ?? "0")} onValueChange={(v) => setRow(l.po_item_id, { tax_rate: v })}>
                            <SelectTrigger data-testid={`ic-inv-tax-${l.po_item_id}`} className="bg-slate-950 border-slate-700 rounded-sm h-8"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm">
                              {TAX_RATES.map((t) => <SelectItem key={t} value={String(t)}>%{t}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            <div className="flex items-center justify-end gap-5 rounded-sm border border-slate-800 bg-slate-950/60 px-4 py-2.5 font-mono text-xs" data-testid="ic-inv-totals">
              <span className="text-slate-500">Ara Toplam <span className="text-slate-200 tabular-nums">₺{fmt(invTotals.sub)}</span></span>
              <span className="text-slate-500">KDV <span className="text-slate-200 tabular-nums">₺{fmt(invTotals.tax)}</span></span>
              <span className="text-slate-400 font-semibold">Genel Toplam <span className="text-emerald-400 tabular-nums">₺{fmt(invTotals.total)}</span></span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvOpen(false)} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm" data-testid="ic-inv-cancel">İptal</Button>
            <Button onClick={submitInvoice} disabled={(saving) || !(can("purchases", "add") || can("finance", "add"))} title={!(can("purchases", "add") || can("finance", "add")) ? "Fatura oluşturma yetkiniz yok (purchases.add veya finance.add)" : undefined} className="bg-blue-600 hover:bg-blue-500 rounded-sm" data-testid="ic-inv-submit">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Faturayı Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Onay / Red dialogu */}
      <Dialog open={actDlg.open} onOpenChange={(v) => { if (!v) setActDlg({ open: false, mode: "", invoice: null }); }}>
        <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 rounded-sm max-w-md" data-testid="ic-action-dialog">
          <DialogHeader>
            <DialogTitle className="text-slate-100">
              {actDlg.mode === "approve" ? "Faturayı Onayla" : "Faturayı Reddet"} — {actDlg.invoice?.invoice_no}
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">
              {actDlg.mode === "approve"
                ? (check?.result === "fark_var" ? "FARK VAR — manuel onay için açıklama zorunludur." : "Fatura uyumlu, doğrudan onaylanacak.")
                : "Ret sebebi zorunludur. Reddedilen fatura Bizim Hesap'a gönderilemez."}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <Label className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">
              {actDlg.mode === "approve" ? "Açıklama" : "Ret Sebebi *"}
            </Label>
            <Textarea data-testid="ic-action-note" value={actNote} onChange={(e) => setActNote(e.target.value)} rows={3}
              className="bg-slate-950 border-slate-700 rounded-sm text-sm mt-1.5"
              placeholder={actDlg.mode === "approve" ? "Fark onay açıklaması…" : "Ret sebebini yazın…"} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActDlg({ open: false, mode: "", invoice: null })} className="border-slate-700 bg-transparent text-slate-300 hover:bg-slate-800 rounded-sm" data-testid="ic-action-cancel">İptal</Button>
            <Button onClick={submitAction} disabled={actSaving}
              className={`rounded-sm ${actDlg.mode === "approve" ? "bg-emerald-600 hover:bg-emerald-500" : "bg-rose-600 hover:bg-rose-500"}`}
              data-testid="ic-action-submit">
              {actSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {actDlg.mode === "approve" ? "Onayla" : "Reddet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
